import {
  Table,
  Thead,
  Td,
  Th,
  Tr,
  Tbody,
  Box,
  Flex,
  Spacer,
  Text,
} from '@chakra-ui/react';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  SortingState,
} from '@tanstack/react-table';
import _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { useCreditFacilityContext } from 'packages/credit-facility/hooks/CreditFacilityProvider';

type SettingsFacilitiesTableProps = {
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug'>[];
};
export const SettingsFacilitiesTable = ({
  creditFacilities,
}: SettingsFacilitiesTableProps) => {
  const navigate = useNavigate();
  const { setCreditFacilityId } = useCreditFacilityContext();

  const [sorting, setSorting] = React.useState<SortingState>([]);

  const creditFacilityColumnDef = [
    {
      accessorKey: 'slug',
      header: 'Facility',
    },
    { accessorKey: 'id', header: 'Id' },
  ];

  // do not display the id in the table, but it will be used as metadata for each row
  const columnVisibility = {
    id: false,
    slug: true,
  };

  const table = useReactTable({
    data: creditFacilities,
    columns: creditFacilityColumnDef,
    state: {
      columnVisibility,
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
  });

  return (
    <Box>
      <Box mt="32px" borderWidth="medium" borderColor={colors.gray[200]}>
        <Table style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <Thead>
            {_.map(table.getHeaderGroups(), (headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const columnCanSort = header.column.getCanSort();

                  return (
                    <Th
                      key={header.id}
                      textTransform="capitalize"
                      letterSpacing="normal"
                      padding="8px 16px 8px 16px"
                      top="0"
                      borderBottomWidth="thin"
                      whiteSpace="nowrap"
                    >
                      <Flex alignItems="center">
                        <Box
                          cursor={columnCanSort ? 'pointer' : 'default'}
                          onClick={header.column.getToggleSortingHandler()}
                          mr="8px"
                        >
                          {{
                            asc: <Icon icon="sort-up" />,
                            desc: <Icon icon="sort-down" />,
                          }[header.column.getIsSorted() as string] ??
                            (columnCanSort ? <Icon icon="sort" /> : null)}
                        </Box>

                        <Box mr="8px">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {_.map(table.getRowModel().rows, (row) => {
              const creditFacilitySlug: string = row.getValue('slug');
              const creditFacilityId: string = row.getValue('id');

              const creditFacilityName = commonUtils.creditFacilitySlugToName(
                creditFacilitySlug,
              );

              const settingsFacilityBorrowingBaseConfigurationRoute = `/settings/credit-facilities/${creditFacilityId}/borrowing-base/configuration` as const;

              return (
                <Tr key={`settings-facilities-table-row-${creditFacilityId}`}>
                  <Td
                    cursor="pointer"
                    fontSize="16px"
                    padding="16px"
                    whiteSpace="nowrap"
                    color={colors.blue[400]}
                    _hover={{
                      backgroundColor: colors.gray[50],
                      textDecoration: 'underline',
                    }}
                    onClick={() => {
                      setCreditFacilityId(creditFacilityId);
                      navigate(settingsFacilityBorrowingBaseConfigurationRoute);
                    }}
                  >
                    <Flex alignItems="center">
                      <Text lineHeight="24px" fontSize="16px">
                        {creditFacilityName}
                      </Text>
                      <Spacer />
                      <Box
                        backgroundColor="white"
                        padding="4px 16px"
                        border="1px"
                        borderColor={colors.aurora[400]}
                        borderRadius="8px"
                        fontSize="14px"
                        fontWeight={600}
                        lineHeight="20px"
                        color={colors.aurora[400]}
                        onClick={() => {
                          navigate(
                            settingsFacilityBorrowingBaseConfigurationRoute,
                          );
                        }}
                      >
                        View Settings
                      </Box>
                    </Flex>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};
